class NostoRecommendations {
  constructor() {
    if (!window.nostojs) {
      throw new Error('Nostro Not initialized')
    }
    this._callback = this._callback.bind(this)
    this._errorCallback = this._errorCallback.bind(this)
    this._makeNostoRequestAndRender()

    this._observeForTabs()
  }

  _observeForTabs() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const nostoTabsContainer = document.querySelector('.ns-tab-btns-container')

          if (nostoTabsContainer && !this._tabsInitialized) {
            this._initTabs()
            observer.disconnect()
          }
        }
      })
    })

    observer.observe(document, {
      childList: true,
      subtree: true
    })

    setTimeout(() => {
      observer.disconnect()
    }, 10000)
  }

  _initTabs() {
    if (this._tabsInitialized) return

    const nostoTabsContainer = document.querySelector('.ns-tab-btns-container')
    const tabs = nostoTabsContainer?.querySelectorAll('.nosto-tab-btn')

    if (!nostoTabsContainer || !tabs || tabs.length === 0) return

    this._tabsInitialized = true

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const placementId = tab.getAttribute('placementid')
        this.loadOnDemand(placementId)
      });
    });

    const firstTab = tabs[0]

    if (firstTab) {
      const firstPlacementId = firstTab.getAttribute('placementid');

      this.loadOnDemand(firstPlacementId);

      const parentElement = nostoTabsContainer.parentElement;
      const placements = parentElement?.querySelector('.nosto-placements');

      const firstPlacement = placements?.querySelector(`#${firstPlacementId}`);

      if (firstPlacement) {
        firstPlacement.classList.add('is-selected');
      }
    }
  }

  loadOnDemand(placementId) {
    return this._makeNostoRequestAndRender([placementId])
  }

  _makeNostoRequestAndRender(placementIDs = []) {
    this._loaded = false
    this._defferedPromise = new Deferred()
    window.nostojs((api) => {
      const request = api.createRecommendationRequest({ includeTagging: true })
      if (Array.isArray(placementIDs) && placementIDs.length > 0) {
        request.setElements(placementIDs)
      }

      request
        .setResponseMode('JSON_ORIGINAL')
        .load()
        .then((res) => {
          const content = {}
          Object.keys(res.recommendations).forEach((divId) => {
            if (res.recommendations[divId]) {
              content[divId] = { ...res.recommendations[divId] }

              if (res.recommendations[divId].products) {
                const customTitle =
                  document.querySelector(`#${divId}`)?.dataset?.title || ''
                const subheading =
                  document.querySelector(`#${divId}`)?.dataset?.subheading || ''
                const generatedHTML = this._createSliderForProducts(
                  res.recommendations[divId],
                  divId,
                  customTitle,
                  subheading
                )
                // Incase there is a error skip this
                if (generatedHTML) {
                  content[divId] = { ...res.recommendations[divId], html: generatedHTML }
                }
              }
            }
          })
          api.placements.injectCampaigns(content)
          this._callback(res)
        })
        .catch(this._errorCallback)
    })
  }

  _nostoCarouselTemplate(placementID = '') {
    let tempElem =
      document.getElementById(`nosto-template-${placementID}`) ||
      document.getElementById('nosto-products-carousel-template')
    return tempElem.innerHTML
  }

  _createSliderForProducts(
    recommendation,
    placementID,
    customTitle,
    subheading
  ) {
    try {
      const htmlString = this._nostoCarouselTemplate(placementID)
      const title = customTitle ?? recommendation.title

      return htmlString
        .replaceAll('###divID###', placementID)
        .replaceAll('###title###', title)
        .replaceAll('###subheading###', subheading)
        .replaceAll('###resultID###', recommendation.result_id)
    } catch (error) {
      console.error(error)
      return null
    }
  }

  _callback(response) {
    this._nostoResponse = response
    this._loaded = true
    this._defferedPromise.resolve(this._nostoResponse)
  }

  _errorCallback(error) {
    this._defferedPromise.reject(error)
  }

  getRecommendation() {
    return new Promise(async (resolve, reject) => {
      if (this._loaded) {
        return resolve(this._nostoResponse)
      }
      try {
        await this._defferedPromise.promise
        return resolve(this._nostoResponse)
      } catch (error) {
        reject(error)
      }
    })
  }

  trackAddToCart(productId, resultID) {
    nostojs(function (api) {
      api.recommendedProductAddedToCart(productId, resultID)
    })
  }

  trackProductView(productId, resultID) {
    nostojs(function (api) {
      api
        .createRecommendationRequest()
        .setProducts([{ product_id: productId }], resultID)
        .load()
    })
  }

  /**
   * Fetch recommendations for a single placement from the Nosto API.
   *
   * This method returns a Promise that resolves with the raw response object
   * returned by the Nosto recommendation request for the provided placement ID.
   * It does not mutate the instance's shared state (e.g. `this._nostoResponse` or
   * `this._loaded`) — callers should treat the returned response as a one-off
   * value and use it directly.
   *
   * @param {{ placementID: string }} options - Options object
   * @param {string} options.placementID - The placement ID to fetch recommendations for
   * @returns {Promise<Object>} Resolves with the Nosto response object for the placement.
   * @rejects {Error} If the underlying Nosto request fails or an exception is thrown.
   *
   * @example
   * // Using async/await from another module/file:
   * // const res = await window.__NOSTO_RECOMMENDER__.fetchSinglePlacement({ placementID: 'nosto-placements-1' });
   */
  fetchSinglePlacement({ placementID }) {
    return new Promise((resolve, reject) => {
      try {
        window.nostojs((api) => {
          const request = api.createRecommendationRequest({ includeTagging: true })
          request.setElements([placementID])

          request
            .setResponseMode('JSON_ORIGINAL')
            .load()
            .then(res => resolve(res))
            .catch(err => reject(err))
        })
      } catch (error) {
        reject(error)
      }
    })
  }
}

window.__NOSTO_RECOMMENDER__ = new NostoRecommendations()
